// What the study says under the board, move by move (docs/STUDY.md, decided 2026-11-01): the repertoire's row and
// the evaluation bar. Pure, so every case has a test; components/study/LivePanel.tsx paints it.

import { winPct } from "./moveRating.ts";

/** What legacy/repertoire.js leaves in `repertoire.status`, as far as this row reads it. */
export interface RepertoireState {
    noRepertoire?: boolean;
    inRepertoire?: boolean;
    endOfRepertoire?: boolean;
    successCount?: number;
    deviatedBy?: "user" | "rival" | null;
    deviation?: { san: string; ply: number; expectedSans: string[] } | null;
    message?: string;
}

export type RowTone = "good" | "bad" | "muted";

export interface RepertoireRow {
    tone: RowTone;
    /** ✓, ✗ or nothing. */
    mark: string;
    text: string;
    /** What the repertoire had where it was left, once it has been left: no longer a spoiler. */
    expected: string | null;
    /** Hint, best move and the list of moves make sense only with repertoire moves to show. */
    tools: boolean;
    /** Taking the move back is offered when it was the user who left. */
    takeBack: boolean;
}

/** "7…O-O" or "7.Nf3": a half move written with its number, from how many came before it in the game. */
export function numberedSan(ply: number, san: string, startTurn: "w" | "b" = "w", startNumber = 1): string {
    const index = ply - 1 + (startTurn === "b" ? 1 : 0);
    const number = startNumber + Math.floor(index / 2);
    return index % 2 === 0 ? `${number}.${san}` : `${number}…${san}`;
}

export function repertoireRow({ loggedIn, state, moves, numbered }: {
    loggedIn: boolean;
    state: RepertoireState | null;
    /** The repertoire's moves from this position. */
    moves: number;
    /** Writes a half move of the game with its number. */
    numbered: (ply: number, san: string) => string;
}): RepertoireRow {
    const row = (over: Partial<RepertoireRow>): RepertoireRow => ({
        tone: "muted", mark: "", text: "", expected: null, tools: false, takeBack: false, ...over,
    });
    if (!loggedIn) return row({ text: "Log in to check this against your repertoire" });
    if (!state || state.noRepertoire) return row({ text: "No repertoire for this position" });

    if (!state.inRepertoire) {
        const d = state.deviation;
        if (state.deviatedBy === "user" && d) {
            return row({
                tone: "bad", mark: "✗", text: `Out of repertoire since ${numbered(d.ply, d.san)}`,
                expected: d.expectedSans.length ? d.expectedSans.join(", ") : null, takeBack: true,
            });
        }
        if (state.deviatedBy === "rival" && d) {
            return row({ tone: "muted", text: `Your opponent left the repertoire with ${numbered(d.ply, d.san)}` });
        }
        return row({ tone: "bad", mark: "✗", text: "Out of repertoire" });
    }
    if (state.endOfRepertoire) return row({ tone: "good", mark: "✓", text: "End of your repertoire" });
    const streak = state.successCount || 0;
    return row({
        tone: "good", mark: "✓",
        text: streak > 0 ? `In repertoire · ${streak} in a row` : "In repertoire",
        tools: moves > 0,
    });
}

export type StopReason = "rare" | "unknown" | "paused" | "repertoire";

export interface PracticeStop {
    title: string;
    text: string;
    /** The button that goes first: taking the move back, or ending the practice where it is. */
    primary: "takeBack" | "finish";
}

/**
 * Why the practice stopped and is waiting for the user, in words: the partner does not move, the practice has not
 * ended, and the two ways on were two small buttons beside the verdict — nobody could tell it was waiting for them
 * (2026-11-01).
 */
export function practiceStop(reason: StopReason | null | undefined, notice: string | null = null): PracticeStop {
    switch (reason) {
        case "repertoire":
            return {
                title: "Not your repertoire's move",
                text: "Your repertoire has another move here. Take it back and find it, or end the practice here.",
                primary: "takeBack",
            };
        case "paused":
            return {
                title: "Lichess is not answering",
                text: notice || "Your opponent's moves come from Lichess, which is not answering right now. "
                    + "Take the move back and try again in a moment, or end the practice here.",
                primary: "takeBack",
            };
        case "unknown":
            return {
                title: "Nobody has played this",
                text: "No game in the explorer reached this position, so your opponent has no real move to answer "
                    + "with. Take it back and try the moves players make, or end the practice here.",
                primary: "takeBack",
            };
        default:
            return {
                title: "Your opponent has no more real moves",
                text: "Too few games reach this position for your opponent to answer with moves players made. "
                    + "End the practice here, or take your move back and try a more common one.",
                primary: "finish",
            };
    }
}

/** How much of the bar is White's, 0 to 100, from a score in pawns from White's side (a mate is the whole bar). */
export function whiteShare(pawns: number | null, mate: boolean): number {
    if (pawns === null) return 50;
    if (mate) return pawns > 0 ? 100 : 0;
    return Math.round(winPct(pawns * 100));
}
